
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmailTemplateType, EmailTemplateProps } from '@/utils/email-templates/types';
import { EmailTemplateEditor } from './EmailTemplateEditor';
import { validateTemplateProps } from '@/utils/email-templates/validator';
import { useEmailTemplates } from '@/hooks/admin/email/useEmailTemplates';
import { toast } from 'sonner';
import { TemplateSelector } from './template/TemplateSelector';
import { TemplatePreview } from './template/TemplatePreview';
import { ValidationResult } from './template/ValidationResult';

export const TemplateManager = () => {
  const { fetchTemplates, saveTemplate, isLoading } = useEmailTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateType>('authSignup');
  const [validationResult, setValidationResult] = useState<{ isValid: boolean; errors: string[] }>({ 
    isValid: false, 
    errors: [] 
  });
  const [previewProps] = useState<EmailTemplateProps>({
    recipientName: 'Test User',
    credentials: { email: 'test@example.com', password: 'password' },
  });
  const [htmlContent, setHtmlContent] = useState('');
  const [textContent, setTextContent] = useState('');
  const [storedTemplates, setStoredTemplates] = useState<any[]>([]);

  useEffect(() => {
    const loadTemplates = async () => {
      const templates = await fetchTemplates();
      if (templates) {
        setStoredTemplates(templates);
      }
    };
    loadTemplates();
  }, []);

  const handleTemplateChange = (templateKey: EmailTemplateType) => {
    setSelectedTemplate(templateKey);
    const template = storedTemplates.find(t => t.type === templateKey);
    
    if (template) {
      setHtmlContent(template.html_content);
      setTextContent(template.text_content);
    } else {
      // Set default content for new templates
      setHtmlContent('<div>New template content</div>');
      setTextContent('New template content');
    }
  };

  const handleValidate = () => {
    try {
      // Ensure we're validating with proper EmailTemplateProps
      const validationResult = validateTemplateProps(previewProps);
      setValidationResult(validationResult);
      
      if (validationResult.isValid) {
        toast.success('Template validation successful!');
      }
    } catch (error: any) {
      setValidationResult({
        isValid: false,
        errors: [error.message || 'Unknown validation error']
      });
    }
  };

  const handleTemplateSave = async () => {
    const saveResult = await saveTemplate(
      selectedTemplate, 
      htmlContent, 
      textContent
    );
    
    if (saveResult) {
      const updatedTemplates = await fetchTemplates();
      if (updatedTemplates) {
        setStoredTemplates(updatedTemplates);
      }
    }
  };

  return (
    <Card className="border-none">
      <CardHeader>
        <CardTitle>Email Template Manager</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="edit" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="edit">Edit Template</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
            <TabsTrigger value="validation">Validation</TabsTrigger>
          </TabsList>

          <TabsContent value="edit" className="space-y-6">
            <TemplateSelector 
              selectedTemplate={selectedTemplate}
              onTemplateChange={handleTemplateChange}
              onValidate={handleValidate}
              onSave={handleTemplateSave}
              isLoading={isLoading}
            />

            <EmailTemplateEditor 
              key={selectedTemplate}
              initialHtml={htmlContent}
              initialText={textContent}
              onHtmlChange={setHtmlContent}
              onTextChange={setTextContent}
            />
          </TabsContent>

          <TabsContent value="preview">
            <TemplatePreview 
              htmlContent={htmlContent}
              textContent={textContent}
            />
          </TabsContent>
          
          <TabsContent value="validation">
            <ValidationResult validationResult={validationResult} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default TemplateManager;
